/* ═══════════════════════════════════════════════════════════
   Tipos e utilitários compartilhados do módulo financeiro
   (multimoeda, fontes de recurso, patrocínios e metas anuais)
   ═══════════════════════════════════════════════════════════ */

export interface MoedaInfo {
  nome: string;
  simbolo: string;
  locale: string;
}

export const MOEDAS: Record<string, MoedaInfo> = {
  BRL: { nome: 'Real brasileiro', simbolo: 'R$', locale: 'pt-BR' },
  EUR: { nome: 'Euro', simbolo: '€', locale: 'de-DE' },
  USD: { nome: 'Dólar americano', simbolo: 'US$', locale: 'en-US' },
  GBP: { nome: 'Libra esterlina', simbolo: '£', locale: 'en-GB' },
  CHF: { nome: 'Franco suíço', simbolo: 'CHF', locale: 'de-CH' },
  ARS: { nome: 'Peso argentino', simbolo: '$', locale: 'es-AR' },
};

export interface FinanceConfig {
  moedaBase: string;
  moedas: string[];
  cotacoes: Record<string, number>;
  /** Quem assina a cobrança (cabeçalho da invoice/ND) — salvo na 1a emissão. */
  emitente?: Record<string, string>;
  /** Para onde o dinheiro deve ir; sai no rodapé do documento. */
  dadosBancarios?: Record<string, string>;
  /** Saldo da fonte no painel: acumulado de todos os anos (padrão) ou só do ano. */
  saldoFonteAcumulado?: boolean;
}

export interface Fonte {
  _id: string;
  nome: string;
  tipo: 'pessoal' | 'loja' | 'patrocinio' | 'banco' | 'cartao' | 'caixa' | 'outro';
  moeda: string;
  saldoInicial: number;
  reembolsavelPadrao: boolean;
  cor: string;
  ativo: boolean;
  observacoes?: string;
}

export interface SaldoFonte extends Fonte {
  /** Saldo somando todos os anos (o que existe no caixa hoje). */
  saldoAcumulado?: number;
  /** Saldo fechado no ano selecionado. */
  saldoNoAno?: number;
  saldoEhAcumulado?: boolean;
  entrou: number;
  saiu: number;
  saldo: number;
  entrouPrevisto: number;
  saiuPrevisto: number;
  saldoPrevisto: number;
  saldoBase: number;
}

export interface Parcela {
  id?: string;
  descricao: string;
  valor: number;
  moeda?: string;
  cotacao?: number;
  valorBase?: number;
  previsao: string;
  status?: 'previsto' | 'recebido';
  dataRecebimento?: string;
  contaId?: string;
}

export interface Contrapartida {
  id?: string;
  descricao: string;
  tipo: 'evento' | 'post' | 'entrega' | 'aparicao' | 'outro';
  quantidade: number;
  concluidas: number;
  prazo: string;
  observacoes?: string;
  concluida?: boolean;
}

export interface Anexo {
  id?: string;
  nome: string;
  url: string;
  tipo?: string;
  tamanho?: number;
  uploadedAt?: string;
}

export interface Patrocinio {
  _id: string;
  patrocinador: string;
  contato: string;
  categoria: string;
  valorTotal: number;
  moeda: string;
  cotacao: number;
  valorTotalBase: number;
  inicio: string;
  fim: string;
  status: 'negociacao' | 'ativo' | 'encerrado' | 'cancelado';
  observacoes: string;
  fonteId?: string;
  parcelas: Parcela[];
  contrapartidas: Contrapartida[];
  anexos: Anexo[];
  resumo?: {
    recebidoBase: number;
    aReceberBase: number;
    parcelasTotal: number;
    parcelasRecebidas: number;
    contrapartidasTotal: number;
    contrapartidasPendentes: number;
    anexos: number;
  };
}

export interface Meta {
  _id: string;
  ano: number;
  categoria: string;
  tipo: 'despesa' | 'receita';
  valor: number;
  moeda: string;
  observacoes?: string;
}

export interface CambioPainel {
  /** Moeda em que o painel está sendo exibido. */
  moeda: string;
  /** De onde veio a cotação (ou null quando não deu para buscar). */
  fonte: string | null;
  atualizadoEm: string | null;
  doCache?: boolean;
  aviso?: string;
  /** cotacao = quantos <moeda> vale 1 unidade da moeda da chave. */
  taxas?: Record<string, number>;
  /** Moedas que apareceram sem cotação conhecida (entraram sem converter). */
  semTaxa?: string[];
}

export interface ResumoAno {
  /** Espelha a configuração usada para calcular o saldo das fontes. */
  saldoFonteAcumulado?: boolean;
  /** Moeda escolhida no painel (pode diferir da moedaBase da empresa). */
  moeda?: string;
  cambio?: CambioPainel;
  /** Anos que têm lançamento — alimenta o seletor do painel. */
  anosComLancamento?: number[];
  ano: number;
  moedaBase: string;
  orcamento: {
    ano: number;
    meta: number;
    realizado: number;
    comprometido: number;
    restante: number;
    pct: number;
    estourou: boolean;
    porCategoria: Array<{
      _id: string; categoria: string; meta: number;
      realizado: number; restante: number; pct: number;
    }>;
  };
  totais: {
    gastoRealizado: number; gastoPrevisto: number;
    receitaRealizada: number; receitaPrevista: number;
  };
  patrocinios: {
    total: number; ativos: number; contratadoBase: number;
    recebidoBase: number; aReceberBase: number;
    contrapartidasPendentes: number; contrapartidasTotal: number;
  };
  contrapartidasAbertas: Array<{
    patrocinioId: string; patrocinador: string; id: string;
    descricao: string; tipo: string; quantidade: number;
    concluidas: number; prazo: string;
  }>;
  reembolsos: { total: number; quantidade: number };
  porMoeda: Array<{ moeda: string; entrou: number; saiu: number; saldo: number }>;
  fontes: SaldoFonte[];
}

/** Formata na moeda informada — nunca assume real. */
export const fmt = (valor: number, moeda = 'BRL') => {
  const info = MOEDAS[moeda] || MOEDAS.BRL;
  try {
    return new Intl.NumberFormat(info.locale, { style: 'currency', currency: moeda }).format(valor || 0);
  } catch {
    // moeda fora do catálogo do navegador: cai no símbolo cru
    return `${info.simbolo} ${(valor || 0).toFixed(2)}`;
  }
};

export const fmtData = (data?: string) => {
  if (!data) return '—';
  // as datas vêm como 'YYYY-MM-DD'; new Date() nessa string usa UTC e
  // volta um dia em fusos negativos — por isso monta manualmente.
  const [ano, mes, dia] = data.slice(0, 10).split('-');
  return ano && mes && dia ? `${dia}/${mes}/${ano}` : data;
};

export const LABEL_TIPO_FONTE: Record<string, string> = {
  pessoal: 'Dinheiro pessoal',
  loja: 'Loja / negócio',
  patrocinio: 'Patrocínio',
  banco: 'Conta bancária',
  cartao: 'Cartão',
  caixa: 'Caixa / espécie',
  outro: 'Outro',
};

export const LABEL_TIPO_CONTRAPARTIDA: Record<string, string> = {
  evento: 'Evento',
  post: 'Publicação',
  entrega: 'Entrega',
  aparicao: 'Aparição',
  outro: 'Outro',
};

export const CORES_STATUS_PATROCINIO: Record<string, string> = {
  negociacao: 'bg-yellow-100 text-yellow-700',
  ativo: 'bg-green-100 text-green-700',
  encerrado: 'bg-gray-100 text-gray-600',
  cancelado: 'bg-red-100 text-red-700',
};

/**
 * Sobe um contrato/comprovante para a pasta da PRÓPRIA empresa.
 * Usa a rota do financeiro (e não o upload genérico do Escalone), porque
 * lá o arquivo fica legível por qualquer um que tenha a URL.
 */
export const uploadAnexo = async (file: File, pasta: string, userEmail: string): Promise<Anexo | null> => {
  const form = new FormData();
  form.append('file', file);
  form.append('pasta', pasta || 'Documentos');
  try {
    const res = await fetch('/api/finance/anexos', {
      method: 'POST',
      headers: { 'X-User-Email': userEmail },
      body: form,
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || !data.success) {
      throw new Error(data.error || 'Falha no envio do arquivo.');
    }
    return {
      nome: data.originalName || data.fileName,
      // guarda a URL limpa: quem abre é quem assina o link (ver linkAnexo)
      url: data.url,
      tipo: data.type,
      tamanho: data.size,
      uploadedAt: data.uploadedAt,
    };
  } catch (e) {
    console.error('Erro ao subir anexo:', e);
    return null;
  }
};

/**
 * Monta o href de um anexo. O navegador não manda header ao abrir um link,
 * então a identidade viaja na query e o servidor confere a empresa antes
 * de entregar o arquivo. Assinado por QUEM ABRE, não por quem subiu.
 */
export const linkAnexo = (url: string, userEmail: string) =>
  url.startsWith('/api/finance/anexos/')
    ? `${url}${url.includes('?') ? '&' : '?'}userEmail=${encodeURIComponent(userEmail)}`
    : url;
